import React, { ReactNode } from 'react'
import styled from 'styled-components'

// Components
import Text from './Text'
import { RowCentered } from './layout/LayoutAtoms'

// Utils
import { useAppState } from '../context/AppContext'

// Types
import { AnyColor } from '../common/models/colors'

interface CircularProgressProps {
  size?: number
  strokeWidth?: number
  value: number
  maxValue: number
  gap?: number // Gap size in degrees
  text?: ReactNode // Content to display in the center
  firstValueName: string
  secondValueName: string
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  size = 230,
  strokeWidth = 21,
  gap = 15,
  text,
  value,
  maxValue,
  firstValueName,
  secondValueName,
}) => {
  const appState = useAppState()
  const { theme } = appState

  const progress = Math.round(((maxValue - value) / maxValue) * 100)
  // Ensure progress values are within valid range
  const boundedProgress1 = Math.min(Math.max(progress, 0), 100)
  const boundedProgress2 = 100 - boundedProgress1

  // Define the fixed start and end angles
  const arcStartAngle = 240
  const arcEndAngle = 120

  // Calculate the total available degrees for the arcs
  const totalAvailableDegrees =
    arcEndAngle >= arcStartAngle
      ? arcEndAngle - arcStartAngle
      : 360 - (arcStartAngle - arcEndAngle)

  // Subtract the gap from available degrees
  const actualAvailableDegrees = totalAvailableDegrees - gap

  // Calculate the degrees for each progress section
  const degrees1 = (boundedProgress1 / 100) * actualAvailableDegrees

  // Calculate the end angle for the first arc
  const endAngle1 = (arcStartAngle + degrees1) % 360

  // Calculate the start angle for the second arc (after the gap)
  const startAngle2 = (endAngle1 + gap) % 360

  // Convert angles to radians and calculate coordinates
  const getCoordinatesForAngle = (angle: number) => {
    const radians = ((angle - 90) * Math.PI) / 180
    const radius = (size - strokeWidth) / 2
    const centerX = size / 2
    const centerY = size / 2

    return {
      x: centerX + radius * Math.cos(radians),
      y: centerY + radius * Math.sin(radians),
    }
  }

  //   // Generate path for an arc
  const createArcPath = (startAngle: number, endAngle: number) => {
    const radius = (size - strokeWidth) / 2

    // Get coordinates
    const start = getCoordinatesForAngle(startAngle)
    const end = getCoordinatesForAngle(endAngle)

    let isLargeArc = 0

    // Check if we're crossing the 0/360 degree point
    if (startAngle > endAngle) {
      // We're crossing the 0/360 point
      isLargeArc = 360 - startAngle + endAngle > 180 ? 1 : 0
    } else {
      // Normal case
      isLargeArc = endAngle - startAngle > 180 ? 1 : 0
    }

    // Create the arc path
    return `
    M ${start.x} ${start.y}
    A ${radius} ${radius} 0 ${isLargeArc} 1 ${end.x} ${end.y}
  `
  }

  return (
    <Container>
      <SvgContainer width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size - strokeWidth) / 2}
          stroke="transparent"
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* First Progress Arc (Red) */}
        {boundedProgress1 > 0 && (
          <path
            d={createArcPath(
              arcStartAngle,
              progress === 100 ? startAngle2 : endAngle1
            )}
            stroke={theme.colors.primaryFadeLight}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
          />
        )}

        {/* Second Progress Arc (Green) */}
        {boundedProgress2 > 0 && (
          <path
            d={createArcPath(
              progress === 0 ? arcStartAngle : startAngle2,
              arcEndAngle
            )}
            stroke={theme.colors.primary}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
          />
        )}

        {/* Center Content */}
        {text && (
          <foreignObject
            x={size / 2 - (size - strokeWidth) / 2}
            y={size / 2 - (size - strokeWidth) / 2}
            width={size - strokeWidth}
            height={size - strokeWidth}
          >
            <CenterContentContainer>
              <div>
                <Text textStyle="displayL" marginRight={5}>
                  {value}
                </Text>
                <Text textStyle="title" color="text72">
                  /{maxValue}
                </Text>
              </div>

              <Text textStyle="caption" textAlign="center">
                {text}
              </Text>
            </CenterContentContainer>
          </foreignObject>
        )}
      </SvgContainer>
      <LegendContainer>
        <Circle color="primaryFadeLight" />
        <Text textStyle="caption" marginRight={22}>
          {firstValueName}
        </Text>
        <Circle color="primary" />
        <Text textStyle="caption">{secondValueName} </Text>
      </LegendContainer>
    </Container>
  )
}

export default CircularProgress

const Container = styled.div`
  position: relative;
  text-align: center;
`

// Styled Components
const SvgContainer = styled.svg`
  overflow: visible;
`

const CenterContentContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 30px;

  & > div:first-of-type {
    display: flex;
    align-items: baseline;
  }
`

export const Circle = styled.div<{ color: AnyColor }>`
  border-radius: 50%;
  width: 16px;
  height: 16px;
  background-color: ${({ color, theme }) => theme.colors[color]};
`

const LegendContainer = styled(RowCentered)`
  margin-top: -30px;
  gap: 11px;
`
